import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertTriangle, CreditCard, LogOut, MessageCircle, RefreshCw, Sparkles, ShieldCheck } from "lucide-react";

/**
 * Shown when a formerly-paid member's subscription has fully expired
 * (users.subscription_status === "canceled"). The most common cause is
 * *failed card renewal* — not a manual cancel — so the page is designed
 * to make the "fix your payment and resume" flow as short as possible.
 *
 * We pull the member's last plan + last payment attempt + saved card
 * hint (via /api/subscription/renewal-context) and pre-populate the CTA
 * so a single tap sends them to `/checkout?renewal=1&plan=<last_plan>`,
 * where the checkout page skips the contact step and pre-selects the
 * previous plan.
 */
const WHATSAPP_URL = "https://wa.me/966538069848?text=" + encodeURIComponent(
  "مرحباً، أواجه مشكلة في تجديد اشتراكي وأحتاج مساعدة."
);

function formatDateAr(iso) {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat("ar-SA", {
      year: "numeric", month: "long", day: "numeric",
    }).format(new Date(iso));
  } catch {
    return null;
  }
}

const CARD_BRAND_ICON = {
  visa: "💳", mastercard: "💳", amex: "💳", mada: "💳",
};

export default function SubscriptionExpiredPage() {
  const { user, logout } = useAuth();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/subscription/renewal-context")
      .then((r) => { if (alive) setCtx(r.data); })
      .catch(() => { if (alive) setCtx({ has_history: false }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const firstName = (user?.full_name || "").split(" ")[0] || "";
  const failedAtAr = formatDateAr(ctx?.payment_failed_at || ctx?.last_paid_at);
  const cardHint = ctx?.card_last4
    ? `${CARD_BRAND_ICON[ctx.card_brand] || "💳"} •••• ${ctx.card_last4}`
    : null;

  // Build the primary CTA URL. If we know the previous plan, drop it into
  // the URL so /checkout preselects it. Otherwise fall back to /checkout.
  const renewHref = ctx?.last_plan_variant
    ? `/checkout?renewal=1&plan=${encodeURIComponent(ctx.last_plan_variant)}&funnel=renewal`
    : "/checkout?renewal=1&funnel=renewal";

  return (
    <div dir="rtl" className="min-h-screen bg-[#0A0A0A] text-white grid place-items-center px-4 py-10 relative overflow-hidden">
      {/* Ambient orange glow */}
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full blur-[130px] opacity-20 pointer-events-none" style={{ background: "#FF6A00" }} aria-hidden />
      <div className="absolute -bottom-32 -right-32 w-[360px] h-[360px] rounded-full blur-[130px] opacity-15 pointer-events-none" style={{ background: "#FF6A00" }} aria-hidden />

      <Card
        className="relative z-10 max-w-xl w-full p-6 sm:p-9 text-center space-y-6 bg-white/[0.04] border-white/10 backdrop-blur"
        data-testid="subscription-expired-page"
      >
        {/* ─── Header ─── */}
        <div className="mx-auto h-14 w-14 rounded-full grid place-items-center" style={{ background: "rgba(251, 191, 36, 0.15)" }}>
          <AlertTriangle className="h-7 w-7" style={{ color: "#FBBF24" }} />
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl sm:text-3xl font-display font-black leading-tight text-white">
            {firstName ? `أهلاً بعودتك ${firstName} 👋` : "أهلاً بعودتك 👋"}
          </h1>
          <p className="text-sm sm:text-base text-white/70 leading-relaxed">
            {user?.trial_expired
              ? "انتهت فترتك التجريبية المجانية — اشترك الآن لمواصلة التعلم بدون انقطاع."
              : ctx?.payment_failed_at
                ? "عذراً — آخر محاولة تجديد لبطاقتك لم تنجح."
                : "اشتراكك متوقف حالياً — نحتاج تحديث الدفع للاستمرار."}
          </p>
        </div>

        {/* ─── Failure context card (only when we have data) ─── */}
        {!loading && ctx?.has_history && (
          <div
            className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3 text-start text-sm"
            data-testid="failure-context"
          >
            {failedAtAr && (
              <Row label="آخر محاولة دفع" value={failedAtAr} />
            )}
            {cardHint && (
              <Row label="البطاقة" value={cardHint} />
            )}
            {ctx.last_amount && ctx.last_currency && (
              <Row
                label="المبلغ"
                value={`${Math.round(ctx.last_amount)} ${ctx.last_currency}`}
              />
            )}
            {ctx.last_plan_label && (
              <Row label="الخطة السابقة" value={ctx.last_plan_label} highlight />
            )}
          </div>
        )}

        {/* ─── Value reminder — what they lose without renewal ─── */}
        <div className="rounded-xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent p-4 text-start space-y-2 text-sm text-white/80">
          <div className="flex items-center gap-2 text-xs font-semibold text-white/60 mb-1">
            <Sparkles className="h-3.5 w-3.5" style={{ color: "#FF6A00" }} />
            <span>ما يعود لك فور التجديد</span>
          </div>
          <BenefitRow>وصول كامل لجميع الدورات المدفوعة</BenefitRow>
          <BenefitRow>استيراد الوصفات من Instagram وTikTok دون قيود</BenefitRow>
          <BenefitRow>شات الذكاء الاصطناعي + مجتمع أعضاء نشط</BenefitRow>
          <BenefitRow>كل ملفاتك ومفضلاتك المحفوظة تعود كما هي</BenefitRow>
        </div>

        {/* ─── Primary CTA ─── */}
        <div className="space-y-2">
          <Button
            asChild
            size="lg"
            className="w-full h-14 gap-2 text-base font-black hover:brightness-110 active:scale-[0.98] transition"
            style={{ background: "#FF6A00", color: "#000", boxShadow: "0 18px 40px -12px #FF6A00" }}
            data-testid="btn-resubscribe"
          >
            <Link to={renewHref}>
              {ctx?.last_plan_variant ? (
                <>
                  <RefreshCw className="h-5 w-5" />
                  جدّد {ctx.last_plan_label || "اشتراكك"} الآن
                </>
              ) : (
                <>
                  <CreditCard className="h-5 w-5" />
                  اختر خطتك وجدّد اشتراكك
                </>
              )}
            </Link>
          </Button>

          {/* Optional secondary — pick a different plan */}
          {ctx?.last_plan_variant && (
            <Button
              asChild
              variant="ghost"
              className="w-full h-11 text-sm text-white/70 hover:text-white hover:bg-white/[0.06]"
              data-testid="btn-pick-different-plan"
            >
              <Link to="/checkout?renewal=1&funnel=renewal">
                أو اختر خطة مختلفة →
              </Link>
            </Button>
          )}
        </div>

        {/* ─── Trust + support ─── */}
        <div className="pt-2 border-t border-white/10 space-y-3">
          <div className="flex items-center justify-center gap-2 text-[11px] text-white/50">
            <ShieldCheck className="h-3.5 w-3.5" style={{ color: "#10B981" }} />
            <span>الدفع مؤمّن عبر Stripe — نفس تجربة الشراء الأصلية</span>
          </div>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors"
            data-testid="link-whatsapp-support"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            مشكلة أثناء الدفع؟ راسلنا في واتساب
          </a>
        </div>

        {/* ─── Logout (tiny, low-priority) ─── */}
        <button
          onClick={logout}
          className="text-[10px] text-white/30 hover:text-white/60 inline-flex items-center gap-1"
          data-testid="btn-logout-expired"
        >
          <LogOut className="h-3 w-3" />
          تسجيل الخروج
        </button>
      </Card>
    </div>
  );
}

// ── Small local presentational helpers (kept in-file to avoid layer creep) ──
function Row({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-white/50 text-xs">{label}</span>
      <span
        className={`font-semibold text-sm ${highlight ? "text-white" : "text-white/85"}`}
        style={highlight ? { color: "#FF6A00" } : undefined}
      >
        {value}
      </span>
    </div>
  );
}

function BenefitRow({ children }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-0.5" style={{ color: "#10B981" }}>✓</span>
      <span>{children}</span>
    </div>
  );
}
